import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fef7ec',
};

const contentStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '18px',
};

const logoStyle = {
  width: '96px',
  height: '96px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '3px solid #e74c3c',
  boxShadow: '0 6px 20px rgba(231, 76, 60, 0.25)',
  animation: 'route-loader-bounce 1s ease-in-out infinite',
};

const spinnerStyle = {
  width: '38px',
  height: '38px',
  border: '4px solid #f3d5cf',
  borderTopColor: '#e74c3c',
  borderRadius: '50%',
  animation: 'route-loader-spin 0.7s linear infinite',
};

const textStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#e74c3c',
  letterSpacing: '0.5px',
};

const loadingPhrases = [
  'searching for restaurants',
  'loading',
  'please wait',
  'processing your order',
];

const isLoadingText = container => {
  if (!container) return true;
  const text = container.innerText || '';
  const lower = text.toLowerCase();
  return loadingPhrases.some(p => lower.includes(p));
};

const hasPageContent = container => {
  if (!container) return false;
  // Find the actual page container (the Switch content)
  const pageRoot = container.querySelector('[data-route-page]');
  const page = pageRoot || container;
  const meaningful = page.querySelectorAll(
    'img:not([data-loader]), .card, .grid, .panda-browse, button, table, form, ul, ol',
  );
  return meaningful.length > 0;
};

const MIN_DISPLAY_MS = 1200;

const waitForContent = () =>
  new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const startedAt = Date.now();
    const safety = setTimeout(finish, 10000);

    const check = () => {
      const container = document.querySelector('.app-content');
      const stillLoading = isLoadingText(container);
      const hasContent = hasPageContent(container);
      if (!stillLoading && hasContent) {
        clearTimeout(safety);
        // Some pages (e.g. checkout, whose data already arrived via router
        // state) render meaningful content within a single frame, which
        // let the loader flash for well under 100ms - long enough to be a
        // visible flicker/jerk on screen but too short to read as an
        // actual loading transition. Always hold the overlay up for at
        // least MIN_DISPLAY_MS from when this route started loading, so
        // every transition feels like a deliberate, smooth handoff.
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(MIN_DISPLAY_MS - elapsed, 80);
        setTimeout(finish, remaining);
      }
    };

    const container = document.querySelector('.app-content');
    if (!container) {
      clearTimeout(safety);
      finish();
      return;
    }

    const observer = new MutationObserver(() => {
      if (settled) return;
      check();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: true,
    });

    const interval = setInterval(() => {
      if (settled) {
        clearInterval(interval);
        return;
      }
      check();
    }, 150);

    requestAnimationFrame(check);
  });

const RouteLoader = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    waitForContent().then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return (
    <>
      {loading && (
        <div style={overlayStyle}>
          <div style={contentStyle}>
            <img
              data-loader
              src="/panda_logo.jpg"
              alt="Panda On Wheels"
              style={logoStyle}
            />
            <div style={spinnerStyle} />
            <p style={textStyle}>Panda is on the way…</p>
          </div>
        </div>
      )}
      <div data-route-page>{children}</div>
    </>
  );
};

export default RouteLoader;
